import { useEffect, useRef, useState } from 'react';
import { logger } from '@/lib/logger';
import type { EcommerceSessionTimeoutResponse } from '@parkingbase/ecommerce-api-test';
import { SessionTimeoutService } from '@/services/session-timeout.service';
import type { ResolvedSessionTimeouts } from './SessionTimeout.types';

/**
 * The company's ECOMMERCE session-timeout settings, as milliseconds.
 *
 * ## Invariants:
 * - `sessionTimeoutEnabled` must be exactly true to enable anything. Why: `undefined` is what
 *   a frontend deployed ahead of the API field sees, and what a company that never opted in
 *   sees. Both must mean "never sign anyone out" — the failure that ejects a paying customer
 *   mid-booking is far worse than the one that leaves a session open.
 * - A rejected request resolves to disabled rather than to platform defaults. Why: the endpoint
 *   answers 404 when a company has saved nothing, which is the normal state, and defaulting
 *   that to "enabled" would switch sign-out on for every company that never asked for it.
 * - Keyed on the company id string only. Why: it is the cache key and the only thing that
 *   changes the answer; the theme object around it churns on every refresh.
 */

/** Per-company, so returning to a company does not refetch. */
const cache = new Map<string, ResolvedSessionTimeouts>();

const DISABLED: ResolvedSessionTimeouts = { enabled: false, idleMs: 0, absoluteMs: 0 };

const MINUTE_MS = 60000;

const toMs = (minutes?: number): number =>
  typeof minutes === 'number' && minutes > 0 ? minutes * MINUTE_MS : 0;

export const resolveTimeouts = (
  config: EcommerceSessionTimeoutResponse | undefined
): ResolvedSessionTimeouts => {
  if (!config || config.sessionTimeoutEnabled !== true) return DISABLED;
  const idleMs = toMs(config.idleTimeoutMinutes);
  const absoluteMs = toMs(config.absoluteTimeoutMinutes);
  // Enabled with both durations cleared would sign the customer out on the next tick; treat
  // it as nothing to enforce instead.
  if (idleMs === 0 && absoluteMs === 0) return DISABLED;
  return { enabled: true, idleMs, absoluteMs };
};

/** Cleared between tests; the cache is otherwise process-lifetime. */
export const clearSessionTimeoutConfigCache = (): void => cache.clear();

export const useSessionTimeoutConfig = (companyId: string): ResolvedSessionTimeouts => {
  const [resolved, setResolved] = useState<ResolvedSessionTimeouts>(
    () => (companyId && cache.get(companyId)) || DISABLED
  );
  // Discards a response that arrives after the customer moved to another company.
  const requestIdRef = useRef(0);

  useEffect(() => {
    // No state reset for a missing company: the return below derives DISABLED from `companyId`,
    // which avoids a cascading render and cannot go stale.
    if (!companyId) return;

    const cached = cache.get(companyId);
    if (cached) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs from the module cache
      setResolved(cached);
      return;
    }

    const requestId = ++requestIdRef.current;
    const service = new SessionTimeoutService();
    service
      .getSessionTimeoutConfig()
      .then((config) => {
        const next = resolveTimeouts(config);
        cache.set(companyId, next);
        if (requestIdRef.current === requestId) setResolved(next);
      })
      .catch((error) => {
        // A company with no saved configuration is the normal case, not an incident, so this
        // is a warning with no toast: nothing is broken for the customer.
        cache.set(companyId, DISABLED);
        if (requestIdRef.current === requestId) setResolved(DISABLED);
        logger.warn('Session timeout configuration unavailable; automatic sign-out stays off', {
          feature: 'session-timeout',
          route: 'getSessionTimeoutConfig',
          error: error instanceof Error ? error.message : String(error),
        });
      });
  }, [companyId]);

  return companyId ? resolved : DISABLED;
};
